import React, { useState, useRef, useEffect } from 'react';
import { User, Globe, LogOut, ChevronDown, BookOpen } from 'lucide-react';
import { useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../translations';

const ProfileDropdown = ({ currentUser, onOpenProfile, onLogout }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const { language, switchLanguage } = useLanguage();
    const t = getTranslation(language);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 bg-white p-1.5 pr-2 md:p-2 md:pr-3 rounded-full border border-slate-100 shadow-sm cursor-pointer hover:border-[#10056E]/30 transition-all"
                data-testid="profile-dropdown-trigger"
            >
                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs">
                    {currentUser?.name?.charAt(0)}
                </div>
                <span className="text-sm font-bold text-slate-700 hidden sm:block">{currentUser?.name}</span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-200/50 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="profile-dropdown-menu">
                    {/* User Profile */}
                    <button
                        onClick={() => { onOpenProfile(); setIsOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                        data-testid="profile-dropdown-profile"
                    >
                        <User size={18} className="text-slate-500" />
                        {t('auth.userProfile')}
                    </button>

                    {/* Language Toggle */}
                    <div className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <Globe size={18} className="text-slate-500" />
                            <span className="text-sm font-medium text-slate-700">{t('auth.language')}</span>
                        </div>
                        <div className="flex items-center bg-slate-100 rounded-full p-0.5" data-testid="language-toggle">
                            <button
                                onClick={() => switchLanguage('en')}
                                className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all ${
                                    language === 'en' 
                                        ? 'bg-[#10056E] text-white shadow-sm' 
                                        : 'text-slate-500 hover:text-slate-700'
                                }`}
                                data-testid="lang-en-btn"
                            >
                                EN
                            </button>
                            <button
                                onClick={() => switchLanguage('ms')}
                                className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all ${
                                    language === 'ms' 
                                        ? 'bg-[#10056E] text-white shadow-sm' 
                                        : 'text-slate-500 hover:text-slate-700'
                                }`}
                                data-testid="lang-ms-btn"
                            >
                                MS
                            </button>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="mx-3 my-1 border-t border-slate-100"></div>

                    {/* User Manual */}
                    <button
                        onClick={() => { window.open(`/manual.html?lang=${language}`, '_blank'); setIsOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                        data-testid="profile-dropdown-manual"
                    >
                        <BookOpen size={18} className="text-slate-500" />
                        {t('auth.userManual')}
                    </button>

                    {/* Divider */}
                    <div className="mx-3 my-1 border-t border-slate-100"></div>

                    {/* Logout */}
                    <button
                        onClick={() => { onLogout(); setIsOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors"
                        data-testid="profile-dropdown-logout"
                    >
                        <LogOut size={18} />
                        {t('auth.logout')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProfileDropdown;
